// src/admin/OfferFeedbackPage.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getAllOfferFeedbacks } from '../api'; // Adjust path if needed

// --- Material UI Imports ---
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import RefreshIcon from '@mui/icons-material/Refresh';
import Pagination from '@mui/material/Pagination';
import Stack from '@mui/material/Stack';

const ITEMS_PER_PAGE = 25;

// Helper function to format date/time
const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
            return 'Invalid Date';
        }
        return date.toLocaleString(undefined, {
            year: 'numeric', month: 'short', day: 'numeric',
            hour: 'numeric', minute: '2-digit', hour12: true
        });
    } catch (e) {
        console.error("Error formatting date:", e);
        return 'Invalid Date';
    }
};

function OfferFeedbackPage() {
    const [feedbacks, setFeedbacks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);

    const fetchFeedbacks = useCallback(async (showLoading = true) => {
        if (showLoading) setLoading(true);
        setError(null);
        try {
            const data = await getAllOfferFeedbacks();
            // API docs say "typically ordered by the submission date, with the most recent feedback appearing first"
            // If not, we can sort here:
            // const sortedData = [...(data || [])].sort((a, b) => new Date(b.submittedDate) - new Date(a.submittedDate));
            setFeedbacks(data || []);
            setCurrentPage(1);
        } catch (err) {
            setError('Failed to load offer feedbacks. Please try refreshing.');
            console.error("Fetch offer feedbacks error:", err.response?.data || err.message || err);
        } finally {
            if (showLoading) setLoading(false);
        }
    }, []);

    useEffect(() => {
        document.title = 'SpinnerCS - Admin: Offer Feedback';
        fetchFeedbacks();
    }, [fetchFeedbacks]);

    const itemsOnPage = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return feedbacks.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [feedbacks, currentPage]);

    const pageCount = useMemo(() => {
        return Math.ceil(feedbacks.length / ITEMS_PER_PAGE);
    }, [feedbacks]);

    const handlePageChange = (event, value) => {
        setCurrentPage(value);
        window.scrollTo(0, 0);
    };

    if (loading && feedbacks.length === 0) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
    }

    return (
        <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>Offer Feedback ({feedbacks.length})</Typography>
                <Tooltip title="Refresh Feedback List">
                    <IconButton onClick={() => fetchFeedbacks(true)} disabled={loading} color="primary">
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            </Stack>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} aria-label="offer feedback table" size="small">
                    <TableHead>
                        <TableRow sx={{ '& th': { fontWeight: 'bold' } }}>
                            <TableCell>Feedback ID</TableCell>
                            <TableCell>Offer ID</TableCell>
                            <TableCell>User ID</TableCell>
                            <TableCell>Comment</TableCell>
                            <TableCell>Submitted Date</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {itemsOnPage.map((feedback) => (
                            <TableRow key={feedback.offerFeedbackId} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                <TableCell>{feedback.offerFeedbackId}</TableCell>
                                <TableCell>{feedback.offerId}</TableCell>
                                <TableCell>{feedback.userId}</TableCell>
                                <TableCell sx={{ maxWidth: 400, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                    {feedback.comment || <em>No comment</em>}
                                </TableCell>
                                <TableCell>{formatDateTime(feedback.submittedDate)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {pageCount > 1 && (
                <Stack spacing={2} sx={{ mt: 3, mb: 2, alignItems: 'center' }}>
                    <Pagination count={pageCount} page={currentPage} onChange={handlePageChange} color="primary" showFirstButton showLastButton disabled={loading} />
                </Stack>
            )}

            {feedbacks.length === 0 && !loading && !error && (
                <Typography sx={{ mt: 3, textAlign: 'center', color: 'text.secondary' }}>
                    No offer feedback found.
                </Typography>
            )}
        </Box>
    );
}

export default OfferFeedbackPage;